import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { Shield, Award, Target, Users, CheckCircle2, BookOpen, BarChart3, Globe } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

const CertificationFrameworkPage = () => {
  const pillars = [
    { icon: Target, title: 'Industry Alignment', desc: 'Certifications mapped to real-world job roles and industry requirements across AI, Data, and Cloud domains.' },
    { icon: BookOpen, title: 'Rigorous Assessment', desc: 'Multi-stage evaluation including theoretical knowledge, practical projects, and live assessments.' },
    { icon: BarChart3, title: 'Structured Levels', desc: 'Progressive certification tiers from Associate to Expert, each with clear competency benchmarks.' },
    { icon: Shield, title: 'Secure Verification', desc: 'Every credential is digitally verifiable through our online portal with unique Certificate IDs and QR codes.' },
  ];

  const methodology = [
    { step: '01', title: 'Enrollment & Training', desc: 'Candidates enroll in a CloudXAI program and complete the prescribed curriculum with hands-on projects.' },
    { step: '02', title: 'Assessment & Evaluation', desc: 'Rigorous multi-stage assessment including theory exam, practical project, and live viva evaluation.' },
    { step: '03', title: 'Certification & Issuance', desc: 'Successful candidates receive a unique Certificate ID and digital credential with QR verification.' },
    { step: '04', title: 'Continuous Validity', desc: 'Credentials are verifiable online anytime. Employers and institutions can validate authenticity instantly.' },
  ];

  return (
    <div className="min-h-screen bg-[#0B0F1A]">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0B0F1A 0%, #020617 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-96 h-96 bg-[#00E5FF] rounded-full blur-[150px]"></div>
          <div className="absolute bottom-20 left-20 w-80 h-80 bg-[#1DE9B6] rounded-full blur-[150px]"></div>
        </div>
        <div className="max-w-7xl mx-auto relative z-10 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-[#00E5FF] to-[#1DE9B6] mb-6 shadow-xl shadow-[#00E5FF]/50">
            <Award className="w-10 h-10 text-[#0B0F1A]" />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 font-['Space_Grotesk']" data-testid="framework-heading">
            CloudXAI Professional <span className="bg-gradient-to-r from-[#00E5FF] to-[#1DE9B6] bg-clip-text text-transparent">Certification Framework</span>
          </h1>
          <p className="text-lg text-[#9CA3AF] max-w-3xl mx-auto mb-8">
            A structured, industry-aligned certification authority specializing in AI, Data, and Cloud technologies, issuing credentials verified through secure digital authentication.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/certifications/levels">
              <Button className="bg-[#00E5FF] hover:bg-[#1DE9B6] text-[#0B0F1A] font-semibold px-8 py-6 text-base rounded-lg shadow-lg shadow-[#00E5FF]/40">
                View Certification Levels
              </Button>
            </Link>
            <Link to="/verify">
              <Button className="border-2 border-[#00E5FF] text-[#00E5FF] hover:bg-[#00E5FF]/10 px-8 py-6 text-base rounded-lg bg-transparent">
                Verify a Certificate
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* About Certification Council */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#111827]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6 font-['Space_Grotesk']">
                About the <span className="bg-gradient-to-r from-[#00E5FF] to-[#1DE9B6] bg-clip-text text-transparent">Certification Council</span>
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-[#00E5FF] to-transparent mb-6"></div>
              <p className="text-[#9CA3AF] leading-relaxed mb-4">
                The CloudXAI Certification Council is an independent body within CloudXAI Technologies that designs, administers, and validates professional certifications in emerging technologies.
              </p>
              <p className="text-[#9CA3AF] leading-relaxed mb-4">
                Our certifications are developed in collaboration with industry experts and aligned with global standards, ensuring that every CloudXAI Certified Professional possesses verified, job-ready skills.
              </p>
              <p className="text-[#9CA3AF] leading-relaxed">
                Every certificate issued carries a unique ID in the format <span className="text-[#00E5FF] font-mono">CXAI-[PROGRAM]-[YEAR]-[NUMBER]</span>, ensuring traceability and instant verification.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: Globe, label: 'Digital Verification', value: 'Online Portal' },
                { icon: Shield, label: 'Security', value: 'QR + ID Based' },
                { icon: Users, label: 'Programs', value: '4 Levels' },
                { icon: CheckCircle2, label: 'Standards', value: 'Industry Aligned' },
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <Card key={i} className="bg-[#0B0F1A] border-2 border-[#00E5FF]/20 hover:border-[#00E5FF]/40 transition-all">
                    <CardContent className="p-6 text-center">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#00E5FF]/20 to-[#1DE9B6]/20 flex items-center justify-center mx-auto mb-3">
                        <Icon className="w-6 h-6 text-[#00E5FF]" />
                      </div>
                      <div className="text-white font-bold text-lg">{item.value}</div>
                      <div className="text-[#9CA3AF] text-sm">{item.label}</div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0B0F1A]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 font-['Space_Grotesk']">
              Framework <span className="bg-gradient-to-r from-[#00E5FF] to-[#1DE9B6] bg-clip-text text-transparent">Pillars</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-[#00E5FF] to-transparent mx-auto mb-4"></div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pillars.map((p, i) => {
              const Icon = p.icon;
              return (
                <Card key={i} className="bg-[#111827] border-2 border-[#00E5FF]/20 hover:border-[#00E5FF]/50 hover:-translate-y-1 transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#00E5FF] to-[#1DE9B6] flex items-center justify-center mb-4">
                      <Icon className="w-7 h-7 text-[#0B0F1A]" />
                    </div>
                    <h3 className="text-white font-bold text-lg mb-2 font-['Space_Grotesk']">{p.title}</h3>
                    <p className="text-[#9CA3AF] text-sm leading-relaxed">{p.desc}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Assessment Methodology */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#111827]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 font-['Space_Grotesk']">
              Assessment <span className="bg-gradient-to-r from-[#00E5FF] to-[#1DE9B6] bg-clip-text text-transparent">Methodology</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-[#00E5FF] to-transparent mx-auto mb-4"></div>
          </div>
          <div className="space-y-8">
            {methodology.map((m, i) => (
              <div key={i} className="flex gap-6 items-start group">
                <div className="flex-shrink-0 w-16 h-16 rounded-2xl bg-gradient-to-br from-[#00E5FF] to-[#1DE9B6] flex items-center justify-center shadow-lg shadow-[#00E5FF]/30 group-hover:shadow-[#00E5FF]/50 transition-all">
                  <span className="text-[#0B0F1A] font-bold text-xl font-['Space_Grotesk']">{m.step}</span>
                </div>
                <div>
                  <h3 className="text-white font-bold text-xl mb-2 font-['Space_Grotesk'] group-hover:text-[#00E5FF] transition-colors">{m.title}</h3>
                  <p className="text-[#9CA3AF] leading-relaxed">{m.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default CertificationFrameworkPage;
